import type { RouteLocationNormalized } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import DashboardView from "../views/DashboardView.vue";
import AuthView from "../views/auth/AuthView.vue";
import { useAuthStore } from "@/stores/useAuthStore";
import type { MoonRouteMeta } from "@/models/moonRouteMeta";
import { RouteName } from "@/router/routeName";
import routeNameToMeta from "@/router/routeNameToMeta";
import hasPermissions from "@/functional/permissions/hasPermissions";
import { USER_HAS_BEEN_ONBOARDED } from "@/functional/storage/localStorageKeys.ts";
import { CHUNK_RELOAD } from "@/functional/storage/sessionStorageKeys.ts";

declare global {
  interface Window {
    heap?: {
      identify: (userId: string) => void;
      addUserProperties: (properties: Record<string, string>) => void;
    };
  }
}

const routeRequiresAuth = (toMeta: MoonRouteMeta): boolean => {
  return toMeta.requiresAuth === undefined ? true : toMeta.requiresAuth;
};

const userHasPermissionToAccessAuthenticatedRoute = (
  currentUserPermissions: string[],
  toMeta: MoonRouteMeta,
): boolean => {
  const requiredPermissions = toMeta.requiredPermissions;
  return hasPermissions(currentUserPermissions, requiredPermissions);
};

const getRouteMeta = (
  route: RouteLocationNormalized,
): MoonRouteMeta | undefined => {
  if (
    route.meta === undefined ||
    !Array.isArray(route.meta?.requiredPermissions)
  ) {
    return undefined;
  }

  return route.meta as MoonRouteMeta;
};

/**
 * Priority order of pages to attempt to start a user who has just logged in on
 */
const landingPagePriorities = [
  RouteName.ORGANISATIONS,
  RouteName.SERVICE_PLAN_LIST,
  RouteName.GROUPS,
  RouteName.USERS,
  RouteName.MY_LEARNING,
  RouteName.MY_PROFILE,
];

/**
 * Get the initial page for a user. This varies depending on a user's permissions.
 * Note, if a user hasn't been hydrated from local storage, this function should
 * always return the AUTH page
 * @param to
 */
const getLandingPageForUser = (
  to: RouteLocationNormalized,
): { name: RouteName } => {
  const fallbackRoute = { name: RouteName.AUTH };
  console.log("defaultRouteRedirect. To:", to.name);
  const routeMeta = getRouteMeta(to);
  if (routeMeta === undefined) {
    console.log("RouteMeta undefined");
    return fallbackRoute;
  }

  // If the user has never logged in before, navigate to the welcome page
  if (localStorage.getItem(USER_HAS_BEEN_ONBOARDED) == null) {
    return { name: RouteName.WELCOME };
  }
  const authStore = useAuthStore();

  for (const routeName of landingPagePriorities) {
    const routeMeta = routeNameToMeta(routeName);
    if (!routeRequiresAuth(routeMeta)) {
      return { name: routeName };
    }

    const userHasPermission = userHasPermissionToAccessAuthenticatedRoute(
      authStore.permissions,
      routeMeta,
    );
    if (userHasPermission) {
      // Skip service plans if the user only has access to 1 service plan
      if (routeName == RouteName.SERVICE_PLAN_LIST) {
        const userServicePlanCount =
          authStore.currentUser?.servicePlans.length ?? 0;

        if (userServicePlanCount < 2) continue;
      }

      return { name: routeName };
    }
    console.log(
      "User" + authStore.currentUser?.publicId + "does not have permission",
    );
  }

  return fallbackRoute;
};

const routes = [
  {
    path: "/",
    name: "dashboard",
    component: DashboardView,
    // redirect done in `beforeRouter` call so that hydrated user permissions can be used to get an appropriate landing page
    meta: routeNameToMeta(RouteName.DASHBOARD),
    children: [
      {
        path: "organisations",
        name: RouteName.ORGANISATIONS,
        meta: routeNameToMeta(RouteName.ORGANISATIONS),
        component: () => import("../views/OrganisationsView.vue"),
        redirect: { name: RouteName.ORGANISATION_LIST },
        children: [
          {
            path: "",
            name: RouteName.ORGANISATION_LIST,
            meta: routeNameToMeta(RouteName.ORGANISATION_LIST),
            component: () => import("../views/OrganisationsListView.vue"),
          },
          {
            path: "new",
            name: RouteName.ORGANISATION_NEW,
            meta: routeNameToMeta(RouteName.ORGANISATION_NEW),
            component: () => import("../views/OrganisationsNewView.vue"),
          },
          {
            path: ":orgId",
            name: RouteName.ORGANISATION_EDIT,
            meta: routeNameToMeta(RouteName.ORGANISATION_EDIT),
            component: () => import("../views/OrganisationsEditView.vue"),
          },
        ],
      },
      {
        path: "servicePlans",
        name: RouteName.SERVICE_PLANS,
        meta: routeNameToMeta(RouteName.SERVICE_PLANS),
        component: () => import("../views/ServicePlansView.vue"),
        redirect: { name: RouteName.SERVICE_PLAN_LIST },
        children: [
          {
            path: ":servicePlanId",
            name: RouteName.SERVICE_PLAN_EDIT,
            meta: routeNameToMeta(RouteName.SERVICE_PLAN_EDIT),
            component: () => import("../views/ServicePlanEditView.vue"),
          },
          {
            path: ":servicePlanId/transferGroups",
            name: RouteName.SERVICE_PLAN_TRANSFER_GROUPS,
            meta: routeNameToMeta(RouteName.SERVICE_PLAN_TRANSFER_GROUPS),
            component: () => import("../views/TransferGroupsView.vue"),
          },
          {
            path: "create",
            name: RouteName.SERVICE_PLAN_CREATE,
            meta: routeNameToMeta(RouteName.SERVICE_PLAN_CREATE),
            component: () => import("../views/ServicePlanCreateView.vue"),
          },
          {
            path: "list",
            name: RouteName.SERVICE_PLAN_LIST,
            meta: routeNameToMeta(RouteName.SERVICE_PLAN_LIST),
            component: () => import("../views/ServicePlansListView.vue"),
          },
        ],
      },
      {
        path: "users/:userId",
        name: RouteName.USER,
        meta: routeNameToMeta(RouteName.USER),
        component: () => import("../views/UserView.vue"),
      },
      {
        path: "/mylearning",
        name: RouteName.MY_LEARNING,
        meta: routeNameToMeta(RouteName.MY_LEARNING),
        component: () => import("../views/UserView.vue"),
      },
      {
        path: "users/:userId/submitscore",
        name: RouteName.USER_SUBMIT_SCORE,
        meta: routeNameToMeta(RouteName.USER_SUBMIT_SCORE),
        component: () => import("../views/UserSubmitScoreView.vue"),
      },
      {
        path: "users/:userId/edit",
        name: RouteName.USER_EDIT,
        meta: routeNameToMeta(RouteName.USER_EDIT),
        component: () => import("../views/UserEditView.vue"),
      },
      {
        path: "users/:userId/suite/:suiteSlug/moduleStats/:moduleSlug",
        name: RouteName.USER_MODULE_STATS,
        meta: routeNameToMeta(RouteName.USER_MODULE_STATS),
        component: () => import("../views/UserModuleStatsView.vue"),
      },
      {
        path: "scoreReports",
        name: RouteName.USER_SCORE_REPORTS,
        meta: routeNameToMeta(RouteName.USER_SCORE_REPORTS),
        component: () => import("../views/UserScoreReportsView.vue"),
      },
      {
        path: "scoreReports/:reportId",
        name: RouteName.USER_SCORE_REPORT,
        meta: routeNameToMeta(RouteName.USER_SCORE_REPORT),
        component: () => import("../views/ScoreReportView.vue"),
      },
      {
        path: "scoreReports/:reportId/heatmap/:blockResultId/:attentionIndex",
        name: RouteName.HEATMAP_VISUALISER,
        meta: routeNameToMeta(RouteName.HEATMAP_VISUALISER),
        component: () => import("../views/HeatmapVisualiserView.vue"),
      },
      {
        path: "users/add",
        name: RouteName.ADD_USERS,
        meta: routeNameToMeta(RouteName.ADD_USERS),
        component: () => import("../views/AddUsersView.vue"),
      },
      {
        path: "/trendsanalytics",
        name: RouteName.TRENDS_ANALYTICS,
        meta: routeNameToMeta(RouteName.TRENDS_ANALYTICS),
        component: () => import("../views/TrendsAnalyticsView.vue"),
      },
      {
        path: "groups",
        name: RouteName.GROUPS,
        meta: routeNameToMeta(RouteName.GROUPS),
        component: () => import("../views/GroupsView.vue"),
        redirect: { name: RouteName.GROUP_LIST },
        children: [
          {
            path: "",
            name: RouteName.GROUP_LIST,
            meta: routeNameToMeta(RouteName.GROUP_LIST),
            component: () => import("../views/GroupsListView.vue"),
          },
          {
            path: ":groupId/users",
            name: RouteName.GROUP_USERS,
            meta: routeNameToMeta(RouteName.GROUP_USERS),
            component: () => import("../views/GroupUsersView.vue"),
          },
          {
            path: ":groupId/edit",
            name: RouteName.GROUP_EDIT,
            meta: routeNameToMeta(RouteName.GROUP_EDIT),
            component: () => import("../views/GroupEditView.vue"),
          },
          {
            path: "create",
            name: RouteName.GROUP_CREATE,
            meta: routeNameToMeta(RouteName.GROUP_CREATE),
            component: () => import("../views/GroupCreateView.vue"),
          },
          {
            path: ":groupId/edit-learners",
            name: RouteName.GROUP_EDIT_LEARNERS,
            meta: routeNameToMeta(RouteName.GROUP_EDIT_LEARNERS),
            component: () => import("../views/GroupEditLearnersView.vue"),
          },
          {
            path: ":groupId/assign-suites",
            name: RouteName.GROUP_ASSIGN_SUITES,
            meta: routeNameToMeta(RouteName.GROUP_ASSIGN_SUITES),
            component: () => import("../views/GroupAssignSuiteView.vue"),
          },
          {
            path: ":groupId/edit-managers",
            name: RouteName.GROUP_EDIT_MANAGERS,
            meta: routeNameToMeta(RouteName.GROUP_EDIT_MANAGERS),
            component: () => import("../views/GroupEditManagersView.vue"),
          },
          {
            path: "trainingtimes",
            name: RouteName.GROUPS_TRAINING_TIMES,
            meta: routeNameToMeta(RouteName.GROUPS_TRAINING_TIMES),
            component: () => import("../views/GroupsTrainingTimesView.vue"),
          },
        ],
      },
      {
        path: "suite-management",
        name: RouteName.SUITE_MANAGEMENT,
        meta: routeNameToMeta(RouteName.SUITE_MANAGEMENT),
        component: () => import("../views/SuiteManagementView.vue"),
        children: [
          {
            path: ":suiteSlug",
            name: RouteName.SUITE_OVERVIEW,
            meta: routeNameToMeta(RouteName.SUITE_OVERVIEW),
            component: () => import("../views/SuiteOverviewView.vue"),
          },
        ],
      },
      {
        path: "/analytics",
        name: RouteName.ANALYTICS,
        meta: routeNameToMeta(RouteName.ANALYTICS),
        redirect: { name: RouteName.ANALYTICS_OVERVIEW },
        component: () => import("../views/AnalyticsView.vue"),
        children: [
          {
            path: "",
            name: RouteName.ANALYTICS_OVERVIEW,
            meta: routeNameToMeta(RouteName.ANALYTICS_OVERVIEW),
            component: () => import("../views/AnalyticsOverviewView.vue"),
          },
          {
            path: "leaderboard",
            name: RouteName.ANALYTICS_LEADERBOARD,
            meta: routeNameToMeta(RouteName.ANALYTICS_LEADERBOARD),
            component: () => import("../views/LeaderboardNewView.vue"),
          },
        ],
      },
      {
        path: "/learningmaterials",
        name: RouteName.LEARNING_MATERIALS,
        meta: routeNameToMeta(RouteName.LEARNING_MATERIALS),
        component: () => import("../views/LearningMaterialsView.vue"),
      },
      {
        path: "/report-generator",
        name: RouteName.REPORT_GENERATOR,
        meta: routeNameToMeta(RouteName.REPORT_GENERATOR),
        component: () => import("../views/ReportGeneratorView.vue"),
      },
      {
        path: "myprofile",
        name: RouteName.MY_PROFILE,
        meta: routeNameToMeta(RouteName.MY_PROFILE),
        component: () => import("../views/MyProfileView.vue"),
      },
      {
        path: "leaderboard",
        name: RouteName.LEADERBOARD,
        meta: routeNameToMeta(RouteName.LEADERBOARD),
        component: () => import("../views/LeaderboardView.vue"),
      },
      {
        path: "welcome",
        name: RouteName.WELCOME,
        meta: routeNameToMeta(RouteName.WELCOME),
        component: () => import("../views/WelcomeView.vue"),
      },
      {
        path: "moonboarding",
        name: RouteName.MOONBOARDING,
        meta: routeNameToMeta(RouteName.MOONBOARDING),
        component: () => import("../views/MoonboardingView.vue"),
      },
      {
        path: "platformissues",
        name: RouteName.PLATFORM_ISSUES,
        meta: routeNameToMeta(RouteName.PLATFORM_ISSUES),
        component: () => import("../views/PlatformIssuesView.vue"),
      },
      {
        path: "system",
        name: RouteName.SYSTEM,
        meta: routeNameToMeta(RouteName.SYSTEM),
        component: () => import("../views/SystemView.vue"),
        redirect: { name: RouteName.SYSTEM_NAV },
        children: [
          {
            path: "",
            name: RouteName.SYSTEM_NAV,
            meta: routeNameToMeta(RouteName.SYSTEM_NAV),
            component: () => import("../views/SystemNavView.vue"),
          },
          {
            path: "content/:suiteSlug/:scenarioSlug/:blockRef?",
            name: RouteName.SYSTEM_CONTENT,
            meta: routeNameToMeta(RouteName.SYSTEM_CONTENT),
            component: () => import("../views/SystemContentView.vue"),
          },
          {
            path: "scenariotrees",
            name: RouteName.SCENARIO_TREES,
            meta: routeNameToMeta(RouteName.SCENARIO_TREES),
            component: () => import("../views/ScenarioTreesView.vue"),
          },
          {
            path: "suiteState",
            name: RouteName.SUITE_STATE,
            meta: routeNameToMeta(RouteName.SUITE_STATE),
            component: () => import("../views/SuiteStateView.vue"),
            children: [
              {
                path: "",
                name: RouteName.SUITE_STATE_LIST,
                meta: routeNameToMeta(RouteName.SUITE_STATE_LIST),
                component: () => import("../views/SuiteStateListView.vue"),
              },
              {
                path: "update",
                name: RouteName.SUITE_STATE_UPDATE,
                meta: routeNameToMeta(RouteName.SUITE_STATE_UPDATE),
                component: () => import("../views/SuiteStateUpdateView.vue"),
              },
            ],
          },
          {
            path: "audit",
            name: RouteName.AUDIT,
            meta: routeNameToMeta(RouteName.AUDIT),
            component: () => import("../views/AuditView.vue"),
          },
          {
            path: "appversions",
            name: RouteName.APP_VERSION,
            meta: routeNameToMeta(RouteName.APP_VERSION),
            component: () => import("../views/AppVersionView.vue"),
          },
          {
            path: "data-operations",
            name: RouteName.DATA_OPERATIONS,
            meta: routeNameToMeta(RouteName.DATA_OPERATIONS),
            component: () => import("../views/DataOperationsView.vue"),
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    name: RouteName.AUTH,
    component: AuthView,
    meta: routeNameToMeta(RouteName.AUTH),
    children: [
      {
        path: "",
        name: RouteName.LOGIN,
        meta: routeNameToMeta(RouteName.LOGIN),
        component: () => import("../views/auth/LoginView.vue"),
      },
      {
        path: "mfa",
        name: RouteName.MFA,
        meta: routeNameToMeta(RouteName.MFA),
        component: () => import("../views/auth/MfaView.vue"),
      },
      {
        path: "recoverycodelogin",
        name: RouteName.RECOVERY_CODE_LOGIN,
        meta: routeNameToMeta(RouteName.RECOVERY_CODE_LOGIN),
        component: () => import("../views/auth/RecoveryCodeLoginView.vue"),
      },
      {
        path: "onboard",
        name: RouteName.USER_ONBOARD,
        meta: routeNameToMeta(RouteName.USER_ONBOARD),
        component: () => import("../views/auth/UserOnboardView.vue"),
      },
      {
        path: "passwordresetrequest",
        name: RouteName.PASSWORD_RESET_REQUEST,
        meta: routeNameToMeta(RouteName.PASSWORD_RESET_REQUEST),
        component: () => import("../views/auth/PasswordResetRequestView.vue"),
      },
      {
        path: "passwordreset",
        name: RouteName.PASSWORD_RESET,
        meta: routeNameToMeta(RouteName.PASSWORD_RESET),
        component: () => import("../views/auth/PasswordResetView.vue"),
      },
      {
        path: "magicLink",
        name: RouteName.MAGIC_LINK,
        meta: routeNameToMeta(RouteName.MAGIC_LINK),
        component: () => import("../views/auth/MagicLinkLoginView.vue"),
      },
    ],
  },
  {
    path: "/organisations/:orgId/login",
    name: RouteName.ORGANISATION_LOGIN,
    meta: routeNameToMeta(RouteName.ORGANISATION_LOGIN),
    component: () => import("../views/auth/OrganisationLoginView.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: RouteName.NOT_FOUND,
    meta: routeNameToMeta(RouteName.NOT_FOUND),
    component: () => import("../views/NotFound.vue"),
  },
];

// Dev-only routes: not included in production builds
if (import.meta.env.DEV) {
  routes.push({
    path: "/dev/icons",
    name: RouteName.DEV_ICONS,
    meta: routeNameToMeta(RouteName.DEV_ICONS),
    component: () => import("../views/DevIcons.vue"),
  });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const CHUNK_LOAD_ERROR =
  /Loading chunk \d+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/;

router.onError((error, to) => {
  if (CHUNK_LOAD_ERROR.test(String(error))) {
    // Guard against infinite reload loops: only reload once per session
    // (unless the variable is cleared by a successful page load)
    const targetPath = to?.fullPath ?? window.location.pathname;
    console.warn(
      "Chunk load error. Likely a stale client after last deploy. Reloading…",
      error,
    );

    if (!sessionStorage.getItem(CHUNK_RELOAD)) {
      // Using session storage (scoped to tab)
      sessionStorage.setItem(CHUNK_RELOAD, targetPath);
      window.location.assign(targetPath);
    } else {
      // Already tried reloading, clear the flag and let the error surface
      sessionStorage.removeItem(CHUNK_RELOAD);
      console.error(
        "Chunk load error persisted after reload. Asset(s) may be missing.",
        error,
      );
    }

    return;
  }

  console.error("Router error:", error);
});

router.afterEach(() => {
  // Note, the expected path for removing the reload guard is via App.vue (with a toast shown)
  sessionStorage.removeItem(CHUNK_RELOAD);
});

router.beforeEach(async (to: RouteLocationNormalized) => {
  const toMeta = getRouteMeta(to);

  if (toMeta === undefined) {
    console.error("Route:", to.name, "incorrectly configured");
    return { name: RouteName.NOT_FOUND };
  }

  const requiresAuth = routeRequiresAuth(toMeta);
  console.log("Route requires auth:", requiresAuth);

  if (requiresAuth) {
    const authStore = useAuthStore();
    try {
      await authStore.hydrateCurrentUserFromStorage();
    } catch (e) {
      // Calling the refreshToken route may not return a successful promise
      console.warn("Error hydrating current user from storage", e);
      return { name: RouteName.LOGIN };
    }
    // We may want to remember the URL that we re-directed from
    if (!authStore.isAuthenticated) {
      console.log(
        "route requires authentication, user is NOT currently authenticated",
      );
      return { name: RouteName.LOGIN };
    }
    console.log(
      "route requires authentication, user is currently authenticated",
    );

    // Check that the current user has the permissions required by the route
    const userHasRequiredPermissions =
      userHasPermissionToAccessAuthenticatedRoute(
        authStore.permissions,
        toMeta,
      );
    if (!userHasRequiredPermissions) {
      console.log("Current permissions:", authStore.permissions);
      console.log("Required permissions:", toMeta.requiredPermissions);
      return { name: RouteName.NOT_FOUND };
    }

    const isHeapDefined = window.heap !== undefined;
    if (isHeapDefined) {
      const userPublicId = authStore.currentUser?.publicId;
      if (userPublicId !== undefined && userPublicId !== null) {
        // Set identity on each authenticated page navigation
        // https://developers.heap.io/docs/using-identify
        window.heap?.identify(userPublicId);

        const organisationPublicId =
          authStore.currentUser?.organisation.publicId;
        if (
          organisationPublicId !== undefined &&
          organisationPublicId !== null
        ) {
          window.heap?.addUserProperties({
            organisation_id: organisationPublicId,
          });
        }
      }
    } else {
      console.warn("Heap is not defined");
    }
  }

  // Note, not using a route `redirect` for this, as we want to hydrate the user before checking
  // their permissions to get the right landing page
  if (to.name === RouteName.DASHBOARD) {
    return getLandingPageForUser(to);
  }
});

export { routes };

export default router;
